import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';
import { useAvailableApps } from '@/hooks/useAvailableApps';
import { useDebounce } from '@/hooks/use-debounce';
import { App } from '@/types/App';

interface AvailableAppsContextValue {
  // App data
  whitelistedApps: App[];
  categoryApps: App[];
  allApps: App[];
  searchResults: App[];
  availableCategories: string[];
  
  // Loading states
  isLoading: boolean;
  isFullyLoaded: boolean;
  isSearching: boolean;
  
  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearchMode: boolean;
  
  // Pagination
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  
  // Error handling
  error: Error | null;
  retry: () => void;
  
  // Debug
  debug?: Record<string, unknown>;
}

const AvailableAppsContext = createContext<AvailableAppsContextValue | null>(null);

interface AvailableAppsProviderProps {
  children: ReactNode;
}

export const AvailableAppsProvider: React.FC<AvailableAppsProviderProps> = ({ children }) => {
  const [inputQuery, setInputQuery] = useState('');
  
  // Debounce search query to avoid excessive API calls
  const debouncedQuery = useDebounce(inputQuery, 300);
  
  // Use the hook with debounced search query
  const {
    whitelistedApps,
    categoryApps,
    allApps,
    searchResults,
    availableCategories,
    isLoading,
    isFullyLoaded,
    isSearchMode,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    retry,
    debug,
  } = useAvailableApps({ searchQuery: debouncedQuery });
  
  // Memoize the setSearchQuery callback
  const setSearchQuery = useCallback((query: string) => {
    setInputQuery(query);
  }, []);
  
  // Is currently searching (input has value but debounce hasn't caught up yet)
  const isSearching = useMemo(() => {
    return inputQuery.length >= 3 && (inputQuery !== debouncedQuery || isLoading);
  }, [inputQuery, debouncedQuery, isLoading]);
  
  const value = useMemo<AvailableAppsContextValue>(() => ({
    whitelistedApps,
    categoryApps,
    allApps,
    searchResults,
    availableCategories,
    isLoading,
    isFullyLoaded,
    isSearching,
    searchQuery: inputQuery,
    setSearchQuery,
    isSearchMode,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    retry,
    debug,
  }), [
    whitelistedApps,
    categoryApps,
    allApps,
    searchResults,
    availableCategories,
    isLoading,
    isFullyLoaded,
    isSearching,
    inputQuery,
    setSearchQuery,
    isSearchMode,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    error,
    retry,
    debug,
  ]);
  
  return (
    <AvailableAppsContext.Provider value={value}>
      {children}
    </AvailableAppsContext.Provider>
  );
};

export const useAvailableAppsContext = (): AvailableAppsContextValue => {
  const context = useContext(AvailableAppsContext);
  if (!context) {
    throw new Error('useAvailableAppsContext must be used within an AvailableAppsProvider');
  }
  return context;
};

export default AvailableAppsContext;
